import { createUrbanPlanningRenderer } from './urban-planning-renderer';
import {
    DEVELOPER_OFFER_MEDIAN_HELP,
    GUNB_EXCERPT_LABEL,
    GUNB_EXCERPT_NOTE,
    bdot10kImpactHelp,
    bdot10kLayerLabel,
    breakableHtml,
    dateLong,
    formatDist,
    gunbExcerptQuote,
    gunbMaskedDescriptionButton,
    parcelOwnershipHelp,
    plural,
    fmtPricePerM2,
    renderInfoHelpButton,
    safeHref,
    streetWithType,
    topographicStoreysToShow,
    TOPOGRAPHIC_STOREYS_HELP,
    TOPOGRAPHIC_STOREYS_HELP_LABEL,
    TOPOGRAPHIC_STOREYS_LABEL,
    transactionAreaLabel,
    transactionPricePerM2Help,
    transactionPricePerM2Label
} from '../utils/formatters';
import { guideLine } from '../../shared/guide-link.js';

// The EGiB outline of a building: shown under this name only, never as its area.
var FOOTPRINT_LABEL = 'Powierzchnia zabudowy (obrys budynku z ewidencji)';
import { EXTERNAL_LINK_NOTES, addSunSimulationStartPoint, buildSunSimulationUrl } from '../utils/url';
import { landUseCategory, landUseDescription, landUseReadable } from '../core/land-use-definitions';

export function createDetailRenderers(config) {
    var state = config.state;
    var urbanPlanningRenderer = createUrbanPlanningRenderer({
        esc: config.esc,
        fmt: config.fmt
    });

    var FAV_ICON = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>';
    var REPORT_ICON = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V4s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></svg>';

    // "Zobacz to miejsce w innych serwisach" for a point on the map.
    function placeExternalLinks(latLng, options) {
        var withPriceRegister = !options || options.priceRegister !== false;
        return config.renderExternalLinks([
            withPriceRegister ? { href: config.rcnMapviewUrl(latLng.lat, latLng.lng), label: 'Rejestr Cen Nieruchomości w Geoportalu', description: EXTERNAL_LINK_NOTES.priceRegister } : null,
            { href: config.geoportalMapviewUrl(latLng.lat, latLng.lng), label: 'Geoportal', description: EXTERNAL_LINK_NOTES.geoportal },
            { href: config.googleMapsUrl(latLng.lat, latLng.lng), label: 'Google Maps', description: EXTERNAL_LINK_NOTES.googleMaps },
        ].filter(Boolean));
    }

    // A value (`html`, already escaped) opens the page the server sent for it;
    // without a safe address it stays text.
    function linkedValue(html, href, title) {
        var safe = safeHref(href);
        if (!safe) return html;
        return '<a class="ms-link" href="' + config.esc(safe) + '"'
            + (title ? ' title="' + config.esc(title) + '"' : '')
            + '>' + html + '</a>';
    }

    // An identifier opens the page of its parcel or building when the server sent
    // one; otherwise it stays plain text. A narrow panel wraps it between its
    // segments („2198_” / „BUD”); its title and address stay plain.
    function identifierValue(text, href, title) {
        return linkedValue(breakableHtml(text), href, title);
    }

    // A value (HTML) and its „?” in one `.ms-help-pair`, so a narrow column wraps
    // the value and keeps the trigger beside it; without help the value alone.
    function withHelp(valueHtml, helpHtml) {
        if (!helpHtml) return valueHtml;
        var first = valueHtml.indexOf('<a ') === 0 ? valueHtml : '<span>' + valueHtml + '</span>';
        return '<span class="ms-help-pair">' + first + helpHtml + '</span>';
    }

    // Placeholder block — filled in by favorites.js after the /by-entity check
    // resolves. Hidden by default; revealed only if the user has favorited this
    // entity. Inserted at the top of each detail panel so the comment shows up
    // right under the title.
    function favoriteNoteSlot() {
        return '<div class="ms-card ms-card--grid nm-detail-block nm-detail-fav-note" data-fav-note hidden>'
            + '  <div class="nm-section-label">W ulubionych</div>'
            + '  <div class="nm-detail-fav-note__body" data-fav-note-body></div>'
            + '</div>';
    }

    function favoriteToggleButton(entityType, entityRef, label) {
        if (entityRef == null || entityRef === '') return '';
        return '<button type="button" class="ms-button ms-button--icon ms-button--pill nm-icon-btn nm-detail-fav-btn"'
            + ' data-rcn-fav-toggle'
            + ' data-fav-entity-type="' + config.esc(entityType) + '"'
            + ' data-fav-entity-ref="' + config.esc(String(entityRef)) + '"'
            + ' data-fav-label="' + config.esc(label || '') + '"'
            + ' aria-pressed="false"'
            + ' aria-label="Dodaj do ulubionych" title="Dodaj do ulubionych">'
            + FAV_ICON + '</button>';
    }

    function renderSignalsSectionShell(htmlParts, signalsContext) {
        if (!config.signalsPanel || !signalsContext || !signalsContext.entityType || signalsContext.entityId == null) {
            return;
        }

        var shell = config.signalsPanel.buildSectionShell(signalsContext);

        // Panel otwarty przez kliknięcie sygnału: sygnały prowadzą stos, żeby to,
        // w co użytkownik kliknął, było widoczne bez przewijania. Dane obiektu
        // (tytuł, linki, wymiary, planowanie) zostają, tylko poniżej.
        if (signalsContext.signalFirst) {
            htmlParts.unshift(shell);
            applySignalHeader(signalsContext);
            return;
        }

        htmlParts.push(shell);
    }

    // Nagłówek panelu mówi wprost, że wybrany został sygnał, a nie sam obiekt.
    // Nazwa kategorii sygnału dochodzi później — dociąga ją handleSignalFocus(),
    // gdy sygnały wrócą z API.
    function applySignalHeader(signalsContext) {
        if (typeof config.setDetailsKicker !== 'function') {
            return;
        }

        config.setDetailsKicker(signalsContext.selectedSignalId
            ? 'Wybrany sygnał społeczności'
            : 'Wybrane sygnały społeczności');
    }



    // options.kicker names the kind of the selected object ("Wybrana działka")
    // and options.department picks the accent of its section of the site.
    function setDetailsMeta(text, options) {
        options = options || {};
        // każda zmiana obiektu chowa link symulacji; budynek/działka włączają go z powrotem
        setSunSimulationLink(null);
        // tak samo kicker wraca do domyślnego tekstu; renderSignalsSectionShell
        // podmienia go z powrotem, jeśli wejście nastąpiło przez sygnał
        if (typeof config.setDetailsKicker === 'function') {
            config.setDetailsKicker(options.kicker || null);
        }
        if (typeof config.setDetailsDepartment === 'function') {
            config.setDetailsDepartment(options.department || null);
        }
        if (typeof config.setDetailsMeta === 'function') {
            config.setDetailsMeta(text);
            return;
        }
        var value = text == null ? '' : String(text);
        config.detailsMetaEl.textContent = value;
    }

    // Link "Symulacja nasłonecznienia 3D" w nagłówku karty szczegółów —
    // dostępny tylko dla budynków i działek EGiB, otwiera symulację pod
    // osobnym adresem z punktem kliknięcia jako miejscem startu obserwatora.
    function setSunSimulationLink(href) {
        var el = document.getElementById('nm-sun-simulation');
        if (!el) {
            return;
        }
        if (href) {
            el.href = href;
            el.hidden = false;
        } else {
            el.removeAttribute('href');
            el.hidden = true;
        }
    }

    // Drive the "Pełne szczegóły" link in the details footer (next to "Pokaż
    // otoczenie"). Surfacing it in the footer keeps it reachable without scrolling
    // to the bottom of the panel; passing a falsy href hides it for objects that
    // have no standalone detail page.
    function setDetailCta(href, label) {
        var el = config.detailCtaEl;
        if (!el) {
            return;
        }
        if (href) {
            el.href = href;
            el.textContent = label || 'Otwórz stronę obiektu';
            el.hidden = false;
        } else {
            el.removeAttribute('href');
            el.hidden = true;
        }
    }

    function renderDetailsPlaceholder() {
        var canSubmitSignal = typeof config.onOpenSubmitForm === 'function';
        var canSubmitListing = typeof config.listingFormUrl === 'string' && config.listingFormUrl !== '';
        var ctaParts = [];

        if (canSubmitListing) {
            ctaParts.push(''
                + '<a class="nm-cta-tile nm-cta-tile--primary" href="' + config.esc(config.listingFormUrl) + '">'
                + '    <span class="nm-cta-tile__icon" aria-hidden="true">'
                + '        <svg width="20" height="20"><use href="#icon-building"/></svg>'
                + '    </span>'
                + '    <span class="nm-cta-tile__body">'
                + '        <strong>Dodaj ogłoszenie</strong>'
                + '        <span>Sprzedaż, wynajem albo zamiana nieruchomości</span>'
                + '    </span>'
                + '</a>');
        }

        if (canSubmitSignal) {
            ctaParts.push(''
                + '<button class="nm-cta-tile" type="button" data-open-signal-submit-global>'
                + '    <span class="nm-cta-tile__icon" aria-hidden="true">'
                + '        <svg width="20" height="20"><use href="#icon-signal"/></svg>'
                + '    </span>'
                + '    <span class="nm-cta-tile__body">'
                + '        <strong>Dodaj uwagę do miejsca</strong>'
                + '        <span>Zgłoś zmianę, problem albo własną obserwację</span>'
                + '    </span>'
                + '</button>');
        }

        var actionsHtml = '';
        if (ctaParts.length > 0) {
            actionsHtml = '<div class="nm-details-empty__divider"><span>albo dodaj nowy obiekt</span></div>'
                + '<div class="nm-details-empty__actions">' + ctaParts.join('') + '</div>';
        }

        setDetailsMeta('');
        config.detailsContentEl.innerHTML = ''
            + '<div class="ms-empty-state nm-empty-state nm-details-empty">'
            + '    <div class="nm-details-empty__intro">'
            + '        <strong>Wybierz miejsce na mapie</strong>'
            + '        <span>Stuknij transakcję, działkę, budynek lub sygnał, aby zobaczyć szczegóły.</span>'
            + '    </div>'
            + actionsHtml
            + '</div>';
        config.openNearbyBtn.disabled = true;
        setDetailCta(null);
    }

    function renderDetailsLoading(label) {
        setDetailsMeta(label || 'Ładowanie szczegółów');
        config.detailsContentEl.innerHTML = '<div class="ms-empty-state nm-loading-state"><strong>Ładowanie…</strong><span>Pobieram szczegóły wybranego obiektu.</span></div>';
        config.openNearbyBtn.disabled = state.getSelectedLatLng() == null;
        setDetailCta(null);
    }

    function renderDetailsError(message) {
        config.detailsContentEl.innerHTML = '<div class="ms-empty-state nm-empty-state"><strong>Nie udało się wczytać szczegółów</strong><span>' + config.esc(message || 'Spróbuj ponownie.') + '</span></div>';
        config.openNearbyBtn.disabled = state.getSelectedLatLng() == null;
        setDetailCta(null);
    }

    function renderParcelDetails(props, options) {
        options = options || {};
        setDetailsMeta('Działka ' + (props.numer || props.id_dzialki || ''), { kicker: 'Wybrana działka', department: 'cadastre' });
        var htmlParts = [];
        var ownershipLabel = props.ownership_label || 'Brak danych';
        var ownershipHelp = parcelOwnershipHelp(props);
        var ownershipRows = '<div class="nm-detail-row"><span>Kategoria własności</span><span class="nm-detail-value--wrap">'
            + config.esc(ownershipLabel)
            + renderInfoHelpButton(ownershipHelp, 'Co oznacza kategoria własności')
            + '</span></div>';
        if (props.grupa_rejestrowa != null) {
            ownershipRows += '<div class="nm-detail-row"><span>Grupa rejestrowa w Ewidencji Gruntów i Budynków</span><span>'
                + config.esc(props.grupa_rejestrowa) + '</span></div>';
        }
        var landUse = landUseCategory(props.klasouzytki_egib);
        var landUseHelp = landUseDescription(props.klasouzytki_egib);
        var landUseRows = '<div class="nm-detail-row"><span>Kategoria użytku gruntowego</span><span class="nm-detail-value--wrap">'
            + config.esc(landUse.label)
            + renderInfoHelpButton(landUseHelp, 'Co oznacza kategoria użytku gruntowego')
            + '</span></div>';
        landUseRows += '<div class="nm-detail-row"><span>Rodzaje użytków</span><span class="nm-detail-value--wrap">'
            + config.esc(landUseReadable(props.klasouzytki_egib)) + '</span></div>';
        var parcelArea = '—';
        if (props.pole_ewid != null) {
            var areaValue = config.fmt(props.pole_ewid) + ' m²';
            parcelArea = areaValue + renderInfoHelpButton(
                'Powierzchnia działki zapisana w ewidencji gruntów i budynków\n'
                + 'Znaczenie: tę samą powierzchnię, w hektarach, podaje wypis z rejestru gruntów; 1 hektar to 10 000 metrów kwadratowych. Porównaj ją z ogłoszeniem i z księgą wieczystą, a cenę za metr kwadratowy licz od niej.\n'
                + 'Uwaga: geodeta po pomiarze granic może ustalić nieco inną powierzchnię.\n'
                + guideLine('slownik#wypis-z-rejestru-gruntow', 'wypis z rejestru gruntów'),
                'Co oznacza powierzchnia ewidencyjna'
            );
        } else if (props.geometry_area_m2 != null) {
            var geometryArea = config.fmt(Math.round(props.geometry_area_m2)) + ' m²';
            parcelArea = geometryArea + ' (orientacyjnie)' + renderInfoHelpButton(
                'Powierzchnia policzona z granic działki na mapie\n'
                + 'Obliczenie: dane ewidencji gruntów i budynków, które pobieramy, nie podają powierzchni tej działki, więc liczymy ją z jej granic.\n'
                + 'Uwaga: wynik jest orientacyjny. Powierzchnię ewidencyjną, od której licz cenę za metr kwadratowy, podaje wypis z rejestru gruntów ze starostwa.\n'
                + guideLine('slownik#wypis-z-rejestru-gruntow', 'wypis z rejestru gruntów'),
                'Skąd orientacyjna powierzchnia działki'
            );
        }
        if (props.id_dzialki) {
            htmlParts.push(config.renderExternalLinks([
                { href: config.geoportalParcelUrl(props.id_dzialki), label: 'Geoportal', description: EXTERNAL_LINK_NOTES.geoportal },
                { href: config.rcnParcelUrl(props.id_dzialki), label: 'Rejestr Cen Nieruchomości w Geoportalu', description: EXTERNAL_LINK_NOTES.priceRegister }
            ]));
        }
        htmlParts.unshift('<div class="ms-card ms-card--grid nm-detail-block">' +
            '<h3 class="nm-detail-title">Działka ' + config.esc(props.numer || props.id_dzialki || '') + '</h3>' +
            '<div class="nm-detail-row"><span>Obręb</span><span class="nm-detail-value--wrap">' + config.esc(props.obreb || '—') + '</span></div>' +
            '<div class="nm-detail-row"><span>Gmina</span><span class="nm-detail-value--wrap">' + config.esc(props.gmina || '—') + '</span></div>' +
            '<div class="nm-detail-row"><span>Powierzchnia ewidencyjna</span><span>' + parcelArea + '</span></div>' +
            ownershipRows +
            landUseRows +
        '</div>' + favoriteNoteSlot());
        if (typeof config.renderParcelMeasurementControls === 'function') {
            htmlParts.push(config.renderParcelMeasurementControls(renderInfoHelpButton(
                'Wymiary działki, odległości od granic i prostokąt domu\n'
                + 'Obliczenie: długość każdej krawędzi liczymy z granic działki w ewidencji gruntów i budynków. Odległość od drogi i od pozostałych granic wpisujesz sam, na początek 5 i 4 metry, a krawędzie od strony drogi wskazujesz na mapie. „Dopasuj największy prostokąt” szuka największego domu o proporcjach podanej szerokości i głębokości, który mieści się w tych odległościach.\n'
                + 'Uwaga: wynik jest orientacyjny. Granice potwierdzi geodeta, a wymagane odległości i położenie domu — plan miejscowy i projektant.\n'
                + guideLine('jak-korzystac-z-metroskopu#dom-na-dzialce', 'jak zmierzyć działkę i ustawić dom'),
                'Jak działają wymiary i odległości na mapie'
            )));
        }
        htmlParts.push(urbanPlanningRenderer.render(props.urbanPlanning || options.urbanPlanning || null));
        renderSignalsSectionShell(htmlParts, options.signalsContext || null);
        var detailHref = config.buildParcelDetailUrl(props);
        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = state.getSelectedLatLng() == null;
        setDetailCta(detailHref, 'Otwórz stronę działki');
        setSunSimulationLink(buildSunSimulationUrl('dzialka', props, state.getSelectedLatLng()));
    }

    function renderBuildingDetails(props, options) {
        options = options || {};
        var selectedLatLng = state.getSelectedLatLng();
        var buildingTypeLabel = props.rodzaj_label;
        setDetailsMeta('Budynek ' + (props.id_budynku || ''), { kicker: 'Wybrany budynek', department: 'cadastre' });
        var htmlParts = [];
        if (selectedLatLng) {
            htmlParts.push(placeExternalLinks(selectedLatLng));
        }
        htmlParts.unshift('<div class="ms-card ms-card--grid nm-detail-block">' +
            '<h3 class="nm-detail-title">Budynek ' + config.esc(props.id_budynku || '') + '</h3>' +
            '<div class="nm-detail-row"><span>Rodzaj</span><span class="nm-detail-value--wrap">' + config.esc(buildingTypeLabel || '—') + '</span></div>' +
            buildingStoreysRow(props) +
        '</div>' + favoriteNoteSlot());
        htmlParts.push(urbanPlanningRenderer.render(props.urbanPlanning || options.urbanPlanning || null));
        renderSignalsSectionShell(htmlParts, options.signalsContext || null);
        var detailHref = config.buildBuildingDetailUrl(props);
        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = selectedLatLng == null;
        setDetailCta(detailHref, 'Otwórz stronę budynku');
        setSunSimulationLink(buildSunSimulationUrl('budynek', props, selectedLatLng));
    }

    function hasParcelDetails(props) {
        return !!(props && (props.obreb || props.gmina || props.pole_ewid != null));
    }

    // The register's storeys, else (none or 0) the BDOT10k value under a label naming that source.
    function buildingStoreysRow(props) {
        var topographic = topographicStoreysToShow(props);
        if (topographic != null) {
            return '<div class="nm-detail-row"><span>' + TOPOGRAPHIC_STOREYS_LABEL
                + renderInfoHelpButton(TOPOGRAPHIC_STOREYS_HELP, TOPOGRAPHIC_STOREYS_HELP_LABEL)
                + '</span><span>' + config.esc(topographic) + '</span></div>';
        }
        return '<div class="nm-detail-row"><span>Kondygnacje</span><span>' + (props.kondygnacje != null ? config.esc(props.kondygnacje) : '—') + '</span></div>';
    }

    function hasBuildingDetails(props) {
        return !!(props && (props.rodzaj || props.kondygnacje != null || props.kondygnacje_bdot10k != null));
    }

    function detailUrlSourceFromData(data) {
        var selectedTransactionRow = state.getSelectedTransactionRow();
        if (selectedTransactionRow && data && selectedTransactionRow.tran_lokalny_id_iip === data.tran_lokalny_id_iip) {
            return selectedTransactionRow;
        }
        if (data && data.items && data.items.length) {
            var first = data.items[0];
            return {
                tran_lokalny_id_iip: data.tran_lokalny_id_iip,
                teryt: first.teryt,
                adres: first.adres,
                powiat_nazwa: first.powiat_nazwa
            };
        }
        return null;
    }

    // Rows of /api/detail items (SzczegolyRenderer::buildItemTypeDetailRows): the
    // identifier rows (`identifier: true`) carry `url` (the EGiB page) and
    // `link_title`, and rows may carry a `hint` for the shared help popup. Only an
    // identifier wraps between its segments; other values stay plain text.
    function renderTransactionItemRows(rows) {
        if (!Array.isArray(rows)) return '';
        return rows.map(function(row) {
            var value = row.identifier === true
                ? identifierValue(row.value, row.url, row.link_title)
                : linkedValue(config.esc(row.value), row.url, row.link_title);
            return '<div class="nm-transaction-element__detail">'
                + '<span>' + config.esc(row.label) + '</span>'
                + '<strong>' + withHelp(value, renderInfoHelpButton(row.hint, 'Wyjaśnienie: ' + row.label)) + '</strong>'
                + '</div>';
        }).join('');
    }

    // Rows of an element's panel that name its price per square metre (the label
    // names the area it is the price of), a price shared by the whole deed and
    // the outline of a building under its own name.
    function transactionItemBasisRows(item, typeRows) {
        var rows = [];
        var assetCategory = item.asset_category != null ? item.asset_category : item.typ;
        if (item.price_per_m2 != null) {
            rows.push({
                label: transactionPricePerM2Label(assetCategory, item.price_per_m2_label),
                value: fmtPricePerM2(item.price_per_m2) + ' zł/m²'
            });
        } else if (item.price_is_shared) {
            rows.push({
                label: 'Cena za metr kwadratowy',
                value: 'brak danych, akt podaje jedną cenę za kilka elementów'
            });
        }
        var hasFootprint = Array.isArray(typeRows) && typeRows.some(function(row) {
            return row && row.label === FOOTPRINT_LABEL;
        });
        if (item.footprint_m2 != null && Number(item.footprint_m2) > 0 && !hasFootprint) {
            rows.push({ label: FOOTPRINT_LABEL, value: config.fmt(item.footprint_m2) + ' m²' });
        }
        return rows;
    }

    function renderTransactionItem(item, index) {
        var kind = item.kind || ({ 1: 'dzialka', 2: 'lokal', 3: 'budynek' }[item.asset_category] || 'inny');
        var icons = { dzialka: '▦', lokal: '▣', budynek: '⌂', inny: '◌' };
        var assetCategory = item.asset_category != null ? item.asset_category : item.typ;
        var details = renderTransactionItemRows(transactionItemBasisRows(item, item.type_detail_rows))
            + renderTransactionItemRows(item.type_detail_rows)
            + renderTransactionItemRows(item.detail_rows);
        var address = item.adres ? config.parseAddr(item.adres) : '';
        var area = item.area != null
            ? '<strong><span class="sr-only">' + config.esc(transactionAreaLabel(assetCategory, item.area_label)) + ': </span>'
                + config.fmt(item.area) + ' <small>m²</small></strong>'
            : '';
        var secondaryMetric = item.price_per_m2 != null
            ? '<span><span class="sr-only">' + config.esc(transactionPricePerM2Label(assetCategory, item.price_per_m2_label)) + ': </span>'
                + fmtPricePerM2(item.price_per_m2) + ' zł/m²</span>'
            : (item.price_is_shared
                ? '<span>jedna cena za cały akt</span>'
                : (item.udzial && item.udzial !== '—' ? '<span>udział ' + config.esc(item.udzial) + '</span>' : ''));
        var panelId = 'nm-transaction-element-' + index;
        var sunSimulationUrl = addSunSimulationStartPoint(item.sun_simulation_url, state.getSelectedLatLng());
        var sunSimulationAction = sunSimulationUrl
            ? '<a class="nm-transaction-element__sun-action" href="' + config.esc(sunSimulationUrl) + '" target="_blank" rel="noopener"'
                + ' aria-label="Otwórz symulację nasłonecznienia 3D"'
                + ' title="Otwórz orientacyjną symulację cienia na podstawie modeli budynków">'
                + '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4"></circle><path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42"></path></svg>'
                + '<span>3D</span></a>'
            : '';

        return '<article class="nm-transaction-element nm-transaction-element--' + config.esc(kind) + '" data-transaction-element>'
            + '<div class="nm-transaction-element__head-row">'
            + '<button type="button" class="nm-transaction-element__head" data-transaction-element-toggle'
            + ' aria-expanded="false" aria-controls="' + panelId + '">'
            + '<span class="nm-transaction-element__icon" aria-hidden="true">' + icons[kind] + '</span>'
            + '<span class="nm-transaction-element__body">'
            + '<span class="nm-transaction-element__top">'
            + '<span class="nm-transaction-element__type">' + config.esc(item.asset_label || config.typLabel(item.asset_category)) + '</span>'
            + (item.numer ? '<strong>' + config.esc(item.numer) + '</strong>' : '')
            + (item.funkcja ? '<span class="nm-transaction-element__function">' + config.esc(item.funkcja) + '</span>' : '')
            + '</span>'
            + (address && address !== '—' ? '<span class="nm-transaction-element__address">' + config.esc(address) + '</span>' : '')
            + '</span>'
            + '<span class="nm-transaction-element__metric">' + area + secondaryMetric + '</span>'
            + '<span class="nm-transaction-element__caret" aria-hidden="true">▾</span>'
            + '</button>'
            + sunSimulationAction
            + '</div>'
            + '<div id="' + panelId + '" class="nm-transaction-element__panel" hidden>' + details + '</div>'
            + '</article>';
    }

    function bindTransactionItemToggles() {
        config.detailsContentEl.querySelectorAll('[data-transaction-element-toggle]').forEach(function(button) {
            button.addEventListener('click', function() {
                var card = button.closest('[data-transaction-element]');
                var panel = card ? card.querySelector('.nm-transaction-element__panel') : null;
                if (!card || !panel) return;
                var open = button.getAttribute('aria-expanded') !== 'true';
                button.setAttribute('aria-expanded', open ? 'true' : 'false');
                card.classList.toggle('is-open', open);
                panel.hidden = !open;
            });
        });
    }

    function renderTransactionDetails(data) {
        var selectedLatLng = state.getSelectedLatLng();
        var htmlParts = [];
        setDetailsMeta('Szczegóły transakcji', { kicker: 'Wybrana transakcja', department: 'transactions' });
        var mainSunSimulationUrl = null;
        if (Array.isArray(data.items)) {
            var sunSimulationItem = data.items.find(function(item) {
                return item && item.kind === 'dzialka' && item.sun_simulation_url;
            }) || data.items.find(function(item) {
                return item && item.sun_simulation_url;
            });
            if (sunSimulationItem) {
                mainSunSimulationUrl = addSunSimulationStartPoint(
                    sunSimulationItem.sun_simulation_url,
                    selectedLatLng
                );
            }
        }
        setSunSimulationLink(mainSunSimulationUrl);

        htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block">');
        htmlParts.push('<div class="nm-detail-title-row">'
            + '<h3 class="nm-detail-title">' + config.esc(data.composition || 'Transakcja') + '</h3>'
            + favoriteToggleButton('transakcja', data.tran_lokalny_id_iip, 'Transakcja ' + (data.tran_lokalny_id_iip || ''))
            + '</div>');
        if (data.tran_cena_brutto != null) {
            // The price per square metre of the header is the price of the area
            // of price_basis, and both are named (never the outline of a building).
            var basis = data.price_basis && typeof data.price_basis === 'object' ? data.price_basis : null;
            var basisLabel = basis ? transactionPricePerM2Label(data.typ, basis.price_per_m2_label) : '';
            htmlParts.push('<div class="nm-detail-price"><strong>' + config.fmt(data.tran_cena_brutto) + ' zł</strong>' +
                (basis && basis.price_per_m2 != null
                    ? '<span><span class="sr-only">' + config.esc(basisLabel) + ': </span>' + fmtPricePerM2(basis.price_per_m2) + ' zł/m²'
                        // The page's own explanation of this pair when it is sent (a
                        // building without a usable area is shown by its land).
                        + renderInfoHelpButton(basis.price_per_m2_help
                            || transactionPricePerM2Help(data.typ, basis.price_per_m2_label, '', basis.price_per_m2_hint, basis.building_share || basis.lokal_share), basisLabel)
                        + '</span>'
                    : '') +
            '</div>');
            if (basis && basis.area != null) {
                htmlParts.push('<div class="nm-detail-row"><span>' + config.esc(transactionAreaLabel(data.typ, basis.area_label)) + '</span>'
                    + '<span>' + config.fmt(basis.area) + ' m²'
                    + (basis.area_hint ? ' (' + config.esc(basis.area_hint) + ')' : '') + '</span></div>');
            }
        }
        var addrSources = [];
        if (data.adres) {
            addrSources.push(data.adres);
        }
        if (Array.isArray(data.items)) {
            data.items.forEach(function(item) {
                if (item && item.adres) {
                    addrSources.push(item.adres);
                }
            });
        }
        var headerAddress = '';
        for (var ai = 0; ai < addrSources.length; ai++) {
            var parsedAddress = config.parseAddr(addrSources[ai]);
            if (parsedAddress && parsedAddress !== '—') {
                headerAddress = parsedAddress;
                break;
            }
        }
        if (headerAddress && typeof config.setDetailsMeta === 'function') {
            // The panel title names the place (the card would repeat it); the
            // deed identifier stays in the summary rows.
            config.setDetailsMeta('Transakcja: ' + headerAddress);
        } else if (headerAddress) {
            htmlParts.push('<div class="nm-detail-address">' + config.esc(headerAddress) + '</div>');
        }
        htmlParts.push('</div>');
        htmlParts.push(favoriteNoteSlot());
        htmlParts.push(urbanPlanningRenderer.render(data.urbanPlanning || null));

        if (Array.isArray(data.summary_rows) && data.summary_rows.length) {
            htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block"><div class="nm-section-label">Podsumowanie</div>');
            data.summary_rows.forEach(function(row) {
                var label = config.esc(row.label);
                if (row.hint) {
                    label += renderInfoHelpButton(row.hint, 'Wyjaśnienie: ' + row.label);
                }
                htmlParts.push('<div class="nm-detail-row"><span>' + label + '</span><span class="nm-detail-value--wrap">' + config.esc(row.value) + '</span></div>');
            });
            htmlParts.push('</div>');
        }

        if (Array.isArray(data.items) && data.items.length) {
            htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block nm-transaction-elements"><div class="nm-section-label">Elementy transakcji</div>');
            data.items.forEach(function(item, index) {
                htmlParts.push(renderTransactionItem(item, index));
            });
            htmlParts.push('</div>');
        }

        if (selectedLatLng) {
            htmlParts.push(placeExternalLinks(selectedLatLng));
        }

        var detailHref = config.buildDetailUrl(detailUrlSourceFromData(data));

        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        bindTransactionItemToggles();
        config.openNearbyBtn.disabled = selectedLatLng == null;
        setDetailCta(detailHref, 'Otwórz stronę transakcji');
    }

    function renderGunbDetails(data) {
        setDetailsMeta('Pozwolenie na budowę ' + (data.numer_decyzji_urzedu || 'numer ' + data.id), { kicker: 'Wybrane pozwolenie na budowę', department: 'permits' });
        var htmlParts = [];

        htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block nm-detail-block--gunb-accent">');
        htmlParts.push('<div class="nm-section-label nm-section-label--gunb">Pozwolenie z Rejestru Głównego Urzędu Nadzoru Budowlanego</div>');
        htmlParts.push('<div class="nm-detail-title-row">'
            + '<h3 class="nm-detail-title">' + config.esc(data.rodzaj_inwestycji || 'Pozwolenie na budowę') + '</h3>'
            + favoriteToggleButton('gunb_permit', data.id, 'Pozwolenie ' + (data.numer_decyzji_urzedu || data.id || ''))
            + '</div>');
        // The API names no private person: the type of works in plain words (`roboty`), the safe
        // excerpt of the register's description (`opis`), an investor only when it is a company or
        // an institution (`inwestor`), and nothing of the designer. The masked description
        // (`opisPelny`) opens from the „?” after the excerpt or, without one, after the works.
        var fullDescription = data.opisPelny ? gunbMaskedDescriptionButton(data.opisPelny, data.opisPelnyZnaczniki) : '';
        if (data.roboty) htmlParts.push('<div class="nm-detail-row"><span>Rodzaj robót</span><span class="nm-detail-value--wrap">' + config.esc(data.roboty) + (data.opis ? '' : fullDescription) + '</span></div>');
        if (data.opis || (!data.roboty && fullDescription)) htmlParts.push('<div class="nm-detail-row"><span>' + GUNB_EXCERPT_LABEL + '</span><span class="nm-detail-value--wrap">' + config.esc(gunbExcerptQuote(data.opis)) + fullDescription + '</span></div>');
        htmlParts.push('<div class="nm-detail-row"><span>Data decyzji</span><span>' + config.esc(data.data_wydania_decyzji || '—') + '</span></div>');
        htmlParts.push('<div class="nm-detail-row"><span>Numer decyzji</span><span>' + config.esc(data.numer_decyzji_urzedu || '—') + '</span></div>');
        // The register's Roman numeral alone means nothing: the category's name (`kategoria_label`)
        // and the annex wording in its „?” (`kategoria_opis`, GunbCategory::help()).
        htmlParts.push('<div class="nm-detail-row"><span>Kategoria obiektu budowlanego</span><span class="nm-detail-value--wrap">'
            + (data.kategoria_label
                ? config.esc(data.kategoria_label) + renderInfoHelpButton(data.kategoria_opis, 'Co oznacza kategoria obiektu budowlanego')
                : '—')
            + '</span></div>');
        if (data.inwestor) htmlParts.push('<div class="nm-detail-row"><span>Inwestor</span><span class="nm-detail-value--wrap">' + config.esc(data.inwestor) + '</span></div>');
        if (data.kubatura) htmlParts.push('<div class="nm-detail-row"><span>Kubatura</span><span>' + config.fmt(data.kubatura) + ' m³</span></div>');
        if (data.opis) htmlParts.push('<p class="nm-detail-copy">' + GUNB_EXCERPT_NOTE + '</p>');
        htmlParts.push('</div>');
        htmlParts.push(favoriteNoteSlot());
        htmlParts.push(urbanPlanningRenderer.render(data.urbanPlanning || null));

        htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block">');
        htmlParts.push('<div class="nm-section-label">Lokalizacja</div>');
        // The register's identifier opens the page of the parcel it is linked to (its exact
        // identifier, its map sheet or the only parcel with its number in the obręb); when EGiB
        // writes that parcel differently, `parcel_hint` explains both spellings in the „?”.
        htmlParts.push('<div class="nm-detail-row"><span>Działka</span><span>'
            + (data.id_dzialki
                ? withHelp(identifierValue(data.id_dzialki, data.parcel_detail_url, 'Otwórz stronę działki'), renderInfoHelpButton(data.parcel_hint, 'Wyjaśnienie: identyfikator działki'))
                : '—')
            + '</span></div>');
        if (data.miasto) htmlParts.push('<div class="nm-detail-row"><span>Miasto</span><span>' + config.esc(data.miasto) + '</span></div>');
        if (data.ulica) {
            // The register keeps the first name or title of a street (`ulica_dalej`) apart from its
            // main part (`ulica`), and the full name starts with it: „ul.”, „Mickiewicza” and „Adama”
            // → „ulica Adama Mickiewicza”, „ul.”, „Maja” and „3” → „ulica 3 Maja”; a type written
            // inside `ulica` („Al. Krakowska”) comes first, in full.
            var street = streetWithType(data.cecha, data.ulica, data.ulica_dalej);
            htmlParts.push('<div class="nm-detail-row"><span>Adres</span><span>' + config.esc([street, data.nr_domu].filter(Boolean).join(' ')) + '</span></div>');
        }
        if (data.numer_arkusza_dzialki) htmlParts.push('<div class="nm-detail-row"><span>Arkusz działki</span><span>' + config.esc(data.numer_arkusza_dzialki) + '</span></div>');
        htmlParts.push('</div>');

        htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block">');
        htmlParts.push('<div class="nm-section-label">Organ wydający</div>');
        htmlParts.push('<div class="nm-detail-row"><span>Nazwa</span><span class="nm-detail-value--wrap">' + config.esc(data.nazwa_organu || '—') + '</span></div>');
        if (data.data_wplywu_wniosku) htmlParts.push('<div class="nm-detail-row"><span>Data wpływu wniosku</span><span>' + config.esc(data.data_wplywu_wniosku) + '</span></div>');
        htmlParts.push('</div>');

        var detailHref = config.buildGunbDetailUrl(data);

        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = state.getSelectedLatLng() == null;
        setDetailCta(detailHref, 'Otwórz stronę pozwolenia');
    }

    function renderOtoczenieDetails(feature) {
        var props = feature && feature.properties ? feature.properties : {};
        var selectedLatLng = state.getSelectedLatLng();
        var label = config.bdot10kLabel(props);
        var detailHref = props.detail_url || null;
        var htmlParts = [];
        var impactSummary = config.bdot10kImpactSummary(props);
        var balanceLabel = config.bdot10kImpactBalanceLabel(props);
        var impactClassificationLabel = config.bdot10kImpactClassificationLabel(props);
        var layerLabel = bdot10kLayerLabel(props) || null;
        var objectLabel = props.object_label || null;
        var impactHelp = bdot10kImpactHelp(props);
        var infoChip = function(labelText, value, popup, chipClass) {
            return '<span class="nm-chip ' + (chipClass || '') + '">'
                + (labelText ? '<b>' + config.esc(labelText) + '</b> ' : '')
                + config.esc(value)
                + renderInfoHelpButton(popup, 'Wyjaśnienie: ' + (labelText || value))
                + '</span>';
        };

        setDetailsMeta('Obiekt otoczenia', { kicker: 'Wybrany obiekt z Bazy obiektów topograficznych', department: 'environment' });

        htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block ' + (props.negative ? 'nm-detail-block--gunb-accent' : '') + '">');
        htmlParts.push('<div class="nm-section-label">Otoczenie</div>');
        htmlParts.push('<div class="nm-detail-title-row">'
            + '<h3 class="nm-detail-title">' + config.esc(label || 'Obiekt sąsiedni') + '</h3>'
            + favoriteToggleButton('bdot10k_object', props.id, label || 'Obiekt Bazy obiektów topograficznych')
            + '</div>');
        if (props.may_not_exist) {
            htmlParts.push('<div class="nm-building-gone-notice"><span class="nm-building-gone-icon" aria-hidden="true">&#9888;</span> Budynek może już nie istnieć</div>');
        }
        if (impactSummary) {
            htmlParts.push('<div class="nm-nearby-meta-row">' +
                (impactClassificationLabel ? infoChip('', impactClassificationLabel, impactHelp.classification, props.negative ? 'nm-chip--danger' : (props.impact_classification === 'useful' ? 'nm-chip--success' : '')) : '') +
                (props.impact_trigger_label ? infoChip('', props.impact_trigger_label,
                    props.impact_trigger === 'cluster' ? impactHelp.clusterTrigger
                        : (props.impact_trigger === 'corridor' ? impactHelp.corridorTrigger : impactHelp.directTrigger),
                    props.impact_trigger === 'cluster' ? 'nm-chip--info' : '') : '') +
                (props.nuisance_level != null ? infoChip('Uciążliwość', props.nuisance_level + '/5', impactHelp.nuisance) : '') +
                (props.usefulness_level != null ? infoChip('Użyteczność', props.usefulness_level + '/5', impactHelp.usefulness) : '') +
                (props.impact_radius != null ? infoChip('Promień wpływu', formatDist(props.impact_radius), impactHelp.radius) : '') +
                (props.cluster_threshold > 1 ? infoChip('Skupisko', (props.same_type_count || 0) + '/' + props.cluster_threshold, impactHelp.cluster) : '') +
                (balanceLabel ? infoChip('Bilans wpływu', balanceLabel, impactHelp.balance) : '') +
            '</div>');
            htmlParts.push('<p class="nm-detail-copy">' + config.esc('Ocena praktyczna: ' + impactSummary + (balanceLabel ? ' (' + balanceLabel + ')' : '') + '.') + '</p>');
        }
        if (props.category_label) htmlParts.push('<div class="nm-detail-row"><span>Kategoria</span><span class="nm-detail-value--wrap">' + config.esc(props.category_label) + '</span></div>');
        if (layerLabel) htmlParts.push('<div class="nm-detail-row"><span>Warstwa</span><span class="nm-detail-value--wrap">' + config.esc(layerLabel) + '</span></div>');
        if (objectLabel && objectLabel !== label) htmlParts.push('<div class="nm-detail-row"><span>Typ obiektu</span><span class="nm-detail-value--wrap">' + config.esc(objectLabel) + '</span></div>');
        if (props.nazwa) htmlParts.push('<div class="nm-detail-row"><span>Nazwa</span><span class="nm-detail-value--wrap">' + config.esc(props.nazwa) + '</span></div>');
        var detailedFunctions = config.renderBdot10kFunctions(props, config.esc);
        if (detailedFunctions) htmlParts.push('<div class="nm-detail-row"><span>Funkcja szczegółowa</span><span class="nm-detail-value--wrap">' + detailedFunctions + '</span></div>');
        var rodzajLabel = props.rodzaj_label || (props.rodzaj ? config.bdot10kTranslate(props.rodzaj) : '');
        if (rodzajLabel) htmlParts.push('<div class="nm-detail-row"><span>Rodzaj</span><span class="nm-detail-value--wrap">' + config.esc(rodzajLabel) + '</span></div>');
        htmlParts.push('</div>');
        htmlParts.push(favoriteNoteSlot());
        htmlParts.push(urbanPlanningRenderer.render(props.urbanPlanning || null));

        if (selectedLatLng) {
            htmlParts.push(placeExternalLinks(selectedLatLng));
        }

        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = selectedLatLng == null;
        setDetailCta(detailHref, 'Otwórz stronę obiektu');
    }

    function formatListingPrice(value, offerType) {
        if (value == null || value === '') {
            return 'Cena do uzgodnienia';
        }
        return config.fmt(value) + (offerType === 'rent' ? ' zł miesięcznie' : ' zł');
    }

    function listingOfferLabel(value) {
        if (value === 'rent') return 'Wynajem';
        if (value === 'sale') return 'Sprzedaż';
        return 'Ogłoszenie';
    }

    var LISTING_CATEGORY_LABELS = {
        apartment: 'Mieszkanie',
        studio: 'Kawalerka',
        room: 'Pokój',
        house: 'Dom',
        development: 'Inwestycja',
        plot: 'Działka',
        commercial_unit: 'Lokal użytkowy',
        warehouse: 'Hala lub magazyn',
        garage: 'Garaż'
    };

    function listingCategoryLabel(value) {
        if (!value) return '—';
        return LISTING_CATEGORY_LABELS[value] || value;
    }

    function listingMediaUrl(path) {
        if (!path) return null;
        var value = String(path);
        if (/^(https?:|data:|\/)/.test(value)) {
            return value;
        }
        return (config.baseUrl || '/') + 'api/listings/media?path=' + encodeURIComponent(value);
    }

    function listingPhotoItems(props) {
        var attachments = Array.isArray(props.attachments) ? props.attachments : [];
        return attachments
            .filter(function(item) {
                return item && (item.detail_path || item.card_path || item.thumbnail_path);
            })
            .map(function(item) {
                var detailUrl = listingMediaUrl(item.detail_path || item.card_path || item.thumbnail_path);
                var thumbUrl = listingMediaUrl(item.thumbnail_path || item.card_path || item.detail_path);
                return detailUrl ? { detail: detailUrl, thumb: thumbUrl || detailUrl } : null;
            })
            .filter(Boolean);
    }

    function renderListingGallery(photos, title, pendingPhotoCount) {
        if (!photos.length) {
            if (Number(pendingPhotoCount || 0) > 0) {
                return '<div class="ms-card ms-card--grid nm-detail-block nm-listing-map-gallery nm-listing-map-gallery--pending">'
                    + '<div class="nm-section-label">Zdjęcia</div>'
                    + '<p class="nm-detail-copy">Zdjęcia są przetwarzane i pojawią się po akceptacji moderacji.</p>'
                    + '</div>';
            }
            return '';
        }
        var photosJson = JSON.stringify(photos).replace(/</g, '\\u003C');

        return '<div class="ms-card ms-card--grid nm-detail-block nm-listing-map-gallery">'
            + '<div class="nm-section-label">Zdjęcia</div>'
            + '<button type="button" class="nm-listing-map-gallery__main" data-map-listing-lightbox-open="0">'
            + '<img src="' + config.esc(photos[0].thumb) + '" alt="' + config.esc(title) + '">'
            + '</button>'
            + (photos.length > 1
                ? '<div class="nm-listing-map-gallery__thumbs">'
                    + photos.slice(1, 5).map(function(photo, index) {
                        return '<button type="button" data-map-listing-lightbox-open="' + (index + 1) + '">'
                            + '<img src="' + config.esc(photo.thumb) + '" alt="">'
                            + '</button>';
                    }).join('')
                + '</div>'
                : '')
            + '<script type="application/json" data-map-listing-lightbox-data>'
            + photosJson
            + '</script>'
            + '</div>';
    }

    /**
     * One investment from the developer-offer layer.
     *
     * A marker stands for the whole investment, so the panel reports counts and a
     * price range and hands the user off to the public catalog page; it never
     * pretends to describe a single flat, and it never links to the admin panel.
     */
    function renderDeveloperInvestmentDetails(props) {
        props = props || {};
        var selectedLatLng = state.getSelectedLatLng();
        var name = props.investment_name || 'Inwestycja deweloperska';
        var detailUrl = props.detail_url || null;
        var approximate = props.geometry_source && props.geometry_source !== 'address_point';
        var offersCount = Number(props.count || 0);
        var htmlParts = [];

        setDetailsMeta(name, { kicker: 'Wybrana inwestycja deweloperska', department: 'offers' });

        // The investment name is the panel's h2; the card opens with the developer.
        htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block nm-detail-block--developer-offer">');
        htmlParts.push('<div class="nm-section-label">Ceny ofertowe dewelopera</div>');
        if (props.developer_name) {
            htmlParts.push('<div class="nm-detail-row"><span>Deweloper</span><span class="nm-detail-value--wrap">' + config.esc(props.developer_name) + '</span></div>');
        }
        if (props.address) {
            htmlParts.push('<div class="nm-detail-row"><span>Adres</span><span class="nm-detail-value--wrap">' + config.esc(props.address) + '</span></div>');
        }
        var unknownHelp = 'Cennik nie mówi, czy te lokale są wolne\n'
            + 'Znaczenie: deweloper nie podał, czy lokal jest wolny, zarezerwowany czy sprzedany. Zanim zaplanujesz zakup, zapytaj dewelopera o konkretny lokal.\n'
            + 'Źródło: cennik dewelopera w portalu dane.gov.pl.';
        var medianHelp = DEVELOPER_OFFER_MEDIAN_HELP;
        var precisionHelp = 'Jak dokładnie znacznik pokazuje położenie inwestycji\n'
            + 'Obliczenie: adres z cennika dopasowujemy do punktów adresowych. Gdy nie ma dokładnego punktu, znacznik stoi przy ulicy, w środku miejscowości albo w jej obszarze.\n'
            + 'Uwaga: przy przybliżonym położeniu znacznik może leżeć daleko od budynków inwestycji. Adres sprawdź w cenniku i na stronie dewelopera.\n'
            + 'Źródło: adres inwestycji z cennika dewelopera.';
        var available = Number(props.available_count || 0);
        var unknown = Number(props.unknown_count || 0);
        htmlParts.push('<div class="nm-detail-row"><span>Oferty w inwestycji</span><span>' + config.esc(String(offersCount)) + '</span></div>');
        htmlParts.push('<div class="nm-detail-row"><span>Dostępność potwierdzona przez dewelopera</span><span>'
            + config.esc(available + ' ' + plural(available, 'oferta', 'oferty', 'ofert')) + '</span></div>');
        htmlParts.push('<div class="nm-detail-row"><span>Dostępność nieznana' + renderInfoHelpButton(unknownHelp, 'Co oznacza nieznana dostępność') + '</span><span>'
            + config.esc(unknown + ' ' + plural(unknown, 'oferta', 'oferty', 'ofert')) + '</span></div>');
        if (props.price_min != null || props.price_max != null) {
            htmlParts.push('<div class="nm-detail-row"><span>Zakres ceny ofertowej</span><span class="nm-detail-value--wrap">'
                + config.esc(formatDeveloperOfferMoney(props.price_min) + ' – ' + formatDeveloperOfferMoney(props.price_max))
                + '</span></div>');
        }
        if (props.price_per_m2_median != null) {
            htmlParts.push('<div class="nm-detail-row"><span>Mediana ceny ofertowej za metr kwadratowy' + renderInfoHelpButton(medianHelp, 'Jak liczymy medianę ceny ofertowej') + '</span><span class="nm-detail-value--wrap">'
                + config.esc(formatDeveloperOfferMoney(props.price_per_m2_median) + '/m²') + '</span></div>');
        }
        htmlParts.push('<div class="nm-detail-row"><span>Precyzja lokalizacji' + renderInfoHelpButton(precisionHelp, 'Co oznacza precyzja lokalizacji') + '</span><span class="nm-detail-value--wrap">'
            + config.esc(developerGeometrySourceLabel(props.geometry_source)) + '</span></div>');
        if (props.last_seen_at) {
            htmlParts.push('<div class="nm-detail-row"><span>Cennik z dnia</span><span>' + config.esc(dateLong(String(props.last_seen_at).substring(0, 10))) + '</span></div>');
        }
        if (approximate) {
            htmlParts.push('<p class="nm-detail-copy">Znacznik pokazuje przybliżone położenie inwestycji.</p>');
        }
        htmlParts.push('<p class="nm-detail-copy">Ceny ofertowe pochodzą z cennika dewelopera opublikowanego w portalu dane.gov.pl. Ceny z aktów notarialnych pokazuje warstwa Transakcje.</p>');
        htmlParts.push('</div>');

        if (selectedLatLng) {
            htmlParts.push(placeExternalLinks(selectedLatLng, { priceRegister: false }));
        }

        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = selectedLatLng == null;
        setDetailCta(detailUrl, 'Otwórz cennik inwestycji');
    }

    function formatDeveloperOfferMoney(value) {
        var num = Number(value);
        if (value == null || value === '' || !isFinite(num)) {
            return 'brak danych';
        }
        return Math.round(num).toLocaleString('pl-PL') + ' zł';
    }

    function developerGeometrySourceLabel(source) {
        if (source === 'address_point') return 'Dokładny punkt adresowy';
        if (source === 'street') return 'Przybliżenie do ulicy';
        if (source === 'locality_centroid') return 'Przybliżenie do środka miejscowości';
        if (source === 'settlement_area') return 'Przybliżenie do obszaru miejscowości';
        return 'Położenie na mapie nieustalone';
    }

    function renderListingDetails(data) {
        var selectedLatLng = state.getSelectedLatLng();
        var props = data && data.properties ? data.properties : (data || {});
        var listingId = props.id || props.listing_id;
        var title = props.title || 'Ogłoszenie nieruchomości';
        var detailUrl = props.detail_url || (props.slug && listingId ? '/ogloszenie/' + props.slug + '-' + listingId : null);
        var photos = listingPhotoItems(props);
        var listingThumb = photos.length ? photos[0].thumb : null;
        var htmlParts = [];

        setDetailsMeta(title, { kicker: 'Wybrane ogłoszenie', department: 'offers' });

        htmlParts.push('<div class="ms-card ms-card--grid nm-detail-block nm-detail-block--listing">');
        htmlParts.push('<div class="nm-listing-card-head' + (listingThumb ? '' : ' nm-listing-card-head--no-thumb') + '">');
        if (listingThumb) {
            htmlParts.push((detailUrl ? '<a class="nm-listing-card-thumb" href="' + config.esc(detailUrl) + '" aria-label="Zobacz ogłoszenie">' : '<div class="nm-listing-card-thumb">')
                + '<img src="' + config.esc(listingThumb) + '" alt="">'
                + (detailUrl ? '</a>' : '</div>'));
        }
        // The listing title is the panel's h2; the card starts with the price.
        var listingPrice = props.price != null ? props.price : props.price_amount;
        var listingArea = props.area_m2;
        var hasPerM2 = props.offer_type !== 'rent' && listingPrice != null && listingPrice !== ''
            && Number(listingArea) > 0 && isFinite(Number(listingPrice));
        var perM2Help = 'Cena za metr kwadratowy z ogłoszenia\n'
            + 'Obliczenie: ' + (hasPerM2
                ? config.fmt(Math.round(Number(listingPrice))) + ' zł ÷ ' + config.fmt(listingArea) + ' m² = ' + fmtPricePerM2(Number(listingPrice) / Number(listingArea)) + ' zł za metr kwadratowy.'
                : 'cena z ogłoszenia podzielona przez powierzchnię; bez jednej z nich nie da się jej policzyć.') + '\n'
            + 'Znaczenie: to cena, której chce sprzedający. Ceny zapłacone za podobne nieruchomości w okolicy pokazuje warstwa Transakcje.\n'
            + 'Źródło: cena i powierzchnia podane w ogłoszeniu.\n'
            + guideLine('slownik#cena-ofertowa', 'cena ofertowa');
        htmlParts.push('<div class="nm-listing-card-main">');
        htmlParts.push('<div class="nm-detail-title-row">'
            + '<div class="nm-detail-price"><strong>' + config.esc(formatListingPrice(listingPrice, props.offer_type)) + '</strong>'
            + '<span>cena ofertowa</span></div>'
            + '<div class="nm-detail-title-actions">'
            + favoriteToggleButton('property_listing', listingId, title)
            + (listingId ? '<button type="button" class="ms-button ms-button--icon ms-button--pill nm-icon-btn nm-detail-report-btn" data-listing-report data-listing-id="' + config.esc(listingId) + '" aria-label="Zgłoś ogłoszenie" title="Zgłoś ogłoszenie">' + REPORT_ICON + '</button>' : '')
            + '</div>'
            + '</div>');
        htmlParts.push('</div></div>');
        htmlParts.push('<div class="nm-detail-row"><span>Rodzaj nieruchomości</span><span>' + config.esc(listingCategoryLabel(props.category)) + '</span></div>');
        htmlParts.push('<div class="nm-detail-row"><span>Oferta</span><span>' + config.esc(listingOfferLabel(props.offer_type)) + '</span></div>');
        htmlParts.push('<div class="nm-detail-row"><span>Powierzchnia</span><span>' + (listingArea != null && listingArea !== '' ? config.esc(config.fmt(listingArea) + ' m²') : 'brak danych') + '</span></div>');
        if (props.offer_type !== 'rent') {
            htmlParts.push('<div class="nm-detail-row"><span>Cena za metr kwadratowy' + renderInfoHelpButton(perM2Help, 'Jak liczymy cenę za metr kwadratowy') + '</span><span>'
                + (hasPerM2 ? config.esc(fmtPricePerM2(Number(listingPrice) / Number(listingArea)) + ' zł/m²') : 'brak danych') + '</span></div>');
        }
        if (props.address || props.address_label) {
            htmlParts.push('<div class="nm-detail-row"><span>Lokalizacja</span><span class="nm-detail-value--wrap">' + config.esc(props.address || props.address_label) + '</span></div>');
        }
        if (props.rooms != null) {
            htmlParts.push('<div class="nm-detail-row"><span>Pokoje</span><span>' + config.esc(props.rooms) + '</span></div>');
        }
        if (detailUrl) {
            htmlParts.push('<a class="nm-listing-card-link" href="' + config.esc(detailUrl) + '">Zobacz całe ogłoszenie</a>');
        }
        htmlParts.push('</div>');
        htmlParts.push(favoriteNoteSlot());
        htmlParts.push(urbanPlanningRenderer.render(props.urbanPlanning || (data && data.urbanPlanning ? data.urbanPlanning : null)));
        htmlParts.push(renderListingGallery(photos, title, props.photos_pending));

        if (selectedLatLng) {
            htmlParts.push(placeExternalLinks(selectedLatLng));
        }

        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = selectedLatLng == null;
        setDetailCta(detailUrl, 'Otwórz pełne ogłoszenie');

        // The panel re-renders asynchronously once the full listing payload
        // arrives — re-apply the favorite state so the bookmark button and
        // the "W ulubionych" note survive the DOM swap.
        if (typeof config.onDetailRendered === 'function') {
            config.onDetailRendered();
        }
    }

    function renderKowrDetails(data) {
        var parcel = data && data.parcel ? data.parcel : {};
        var localParcel = data && data.local_parcel ? data.local_parcel : null;
        var listings = data && Array.isArray(data.listings) ? data.listings : [];
        var identifier = parcel.identifier || '';
        var htmlParts = [];

        setDetailsMeta('Działka ' + identifier, { kicker: 'Działka w zasobie Krajowego Ośrodka Wsparcia Rolnictwa', department: 'cadastre' });

        var identifierHelp = renderInfoHelpButton(
            'Działka należy do państwowego zasobu gruntów rolnych\n'
            + 'Znaczenie: gospodaruje nią Krajowy Ośrodek Wsparcia Rolnictwa, który może takie grunty wydzierżawić. Sprzedaż państwowych gruntów rolnych jest wstrzymana do 30 kwietnia 2036 roku, z wyjątkami, między innymi dla nieruchomości do 5 hektarów.\n'
            + 'Uwaga: obecność w warstwie nie oznacza, że działka jest teraz na sprzedaż albo do dzierżawy; ogłoszenia sprawdzisz w portalu eRolnik.\n'
            + 'Źródło: usługa mapowa Krajowego Ośrodka Wsparcia Rolnictwa w Geoportalu.\n'
            + guideLine('slownik#kowr', 'Krajowy Ośrodek Wsparcia Rolnictwa'),
            'Co oznacza obecność działki w tej warstwie'
        );

        htmlParts.push('<section class="ms-card ms-card--grid nm-detail-block">'
            + '<div class="nm-section-label">Zasób Własności Rolnej Skarbu Państwa</div>'
            + '<h3 class="nm-detail-title">Działka w zasobie Krajowego Ośrodka Wsparcia Rolnictwa</h3>'
            + '<div class="nm-detail-row"><span>Identyfikator działki</span><span class="nm-detail-value--wrap">'
            + withHelp(identifier ? identifierValue(identifier, localParcel && localParcel.detail_url, 'Otwórz stronę działki') : '—', identifierHelp)
            + '</span></div>'
            + (localParcel && localParcel.number
                ? '<div class="nm-detail-row"><span>Numer działki</span><span>' + config.esc(localParcel.number) + '</span></div>'
                : '')
            + (localParcel && localParcel.cadastral_district
                ? '<div class="nm-detail-row"><span>Obręb ewidencyjny</span><span class="nm-detail-value--wrap">' + config.esc(localParcel.cadastral_district) + '</span></div>'
                : '')
            + (localParcel && localParcel.municipality
                ? '<div class="nm-detail-row"><span>Gmina</span><span class="nm-detail-value--wrap">' + config.esc(localParcel.municipality) + '</span></div>'
                : '')
            + (localParcel && localParcel.area_m2 != null
                ? '<div class="nm-detail-row"><span>Powierzchnia ewidencyjna</span><span>' + config.fmt(localParcel.area_m2) + ' m²</span></div>'
                : '')
            + (parcel.data_state
                ? '<div class="nm-detail-row"><span>Aktualność warstwy</span><span class="nm-detail-value--wrap">' + config.esc(parcel.data_state) + '</span></div>'
                : '')
            + '<p class="nm-detail-copy">Warstwa wskazuje działki należące do zasobu państwowego. Informację o aktualnej formie zagospodarowania lub dostępności należy potwierdzić w portalu eRolnik.</p>'
            + '</section>');

        if (listings.length) {
            htmlParts.push('<section class="ms-card ms-card--grid nm-detail-block">'
                + '<div class="nm-section-label">Powiązane aktualne ogłoszenia</div>'
                + '<h3 class="nm-detail-title">Ogłoszenia eRolnik dotyczące tej działki</h3>'
                + '<p class="nm-detail-copy">Poniższe ogłoszenia zostały zaimportowane i połączone z działką na podstawie jej pełnego identyfikatora ewidencyjnego.</p>'
                + listings.map(function(listing) {
                    var detailUrl = listing.detail_url || null;
                    return '<article class="ms-card ms-card--grid nm-list-card nm-listing-card">'
                        + '<div class="nm-list-card-top"><span class="nm-pill">Ogłoszenie eRolnik</span></div>'
                        + '<div class="nm-list-address">' + config.esc(listing.title || 'Ogłoszenie dotyczące działki') + '</div>'
                        + '<div class="nm-list-meta-row">'
                        + '<span class="nm-chip"><b>Cena ofertowa</b> ' + config.esc(formatListingPrice(listing.price, listing.offer_type)) + '</span>'
                        + (listing.area_m2 != null ? '<span class="nm-chip"><b>Powierzchnia</b> ' + config.fmt(listing.area_m2) + ' m²</span>' : '')
                        + '</div>'
                        + (detailUrl ? '<a class="nm-listing-card-link" href="' + config.esc(detailUrl) + '">Zobacz pełne ogłoszenie w Metroskopie</a>' : '')
                        + (listing.source_url ? '<a class="ms-link nm-ext-link nm-ext-link--compact" href="' + config.esc(listing.source_url) + '" target="_blank" rel="noopener">Otwórz źródło w portalu eRolnik ↗</a>' : '')
                        + '</article>';
                }).join('')
                + '</section>');
        } else {
            htmlParts.push('<section class="ms-card ms-card--grid nm-detail-block">'
                + '<div class="nm-section-label">Ogłoszenia</div>'
                + '<h3 class="nm-detail-title">Sprawdź aktualną dostępność działki</h3>'
                + (parcel.erolnik_url
                    ? '<a class="ms-link nm-ext-link" href="' + config.esc(parcel.erolnik_url) + '" target="_blank" rel="noopener">Sprawdź działkę w portalu eRolnik ↗</a>'
                    : '')
                + '</section>');
        }

        htmlParts.push(config.renderExternalLinks([
            localParcel && localParcel.detail_url ? { href: localParcel.detail_url, label: 'Pełne informacje o działce w Metroskopie' } : null,
            listings.length && parcel.erolnik_url ? { href: parcel.erolnik_url, label: 'Wykaz działki w portalu eRolnik' } : null,
            identifier ? { href: config.geoportalParcelUrl(identifier), label: 'Działka w Geoportalu', description: EXTERNAL_LINK_NOTES.geoportal } : null,
        ].filter(Boolean)));

        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = state.getSelectedLatLng() == null;
        setDetailCta(localParcel && localParcel.detail_url ? localParcel.detail_url : null, 'Otwórz stronę działki');
    }

    function renderSignalTargetDetails(props, signalsContext) {
        var selectedLatLng = state.getSelectedLatLng();
        var htmlParts = [];
        var targetTypeLabel = signalsContext && signalsContext.entityType === 'miejscowosc'
            ? 'Miejscowość'
            : 'Punkt na mapie';

        setDetailsMeta(targetTypeLabel, { kicker: 'Sygnały społeczności', department: 'signals' });

        htmlParts.push(urbanPlanningRenderer.render(props && props.urbanPlanning ? props.urbanPlanning : null));
        renderSignalsSectionShell(htmlParts, signalsContext || null);

        if (selectedLatLng) {
            htmlParts.push(placeExternalLinks(selectedLatLng));
        }

        config.detailsContentEl.innerHTML = '<div class="nm-detail-stack">' + htmlParts.join('') + '</div>';
        config.openNearbyBtn.disabled = selectedLatLng == null;
        setDetailCta(null);
    }


    return {
        hasBuildingDetails: hasBuildingDetails,
        hasParcelDetails: hasParcelDetails,
        renderBuildingDetails: renderBuildingDetails,
        renderDetailsError: renderDetailsError,
        renderDeveloperInvestmentDetails: renderDeveloperInvestmentDetails,
        renderDetailsLoading: renderDetailsLoading,
        renderDetailsPlaceholder: renderDetailsPlaceholder,
        renderGunbDetails: renderGunbDetails,
        renderKowrDetails: renderKowrDetails,
        renderListingDetails: renderListingDetails,
        renderOtoczenieDetails: renderOtoczenieDetails,
        renderParcelDetails: renderParcelDetails,
        renderSignalTargetDetails: renderSignalTargetDetails,
        renderTransactionDetails: renderTransactionDetails,
    };
}
